import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Badge, Button, Card, Group, Loader, SegmentedControl, SimpleGrid, Stack, Table, Text, TextInput, ThemeIcon, Title,
} from '@mantine/core';
import { IconArrowRight, IconKey, IconLock, IconSearch, IconShieldCheck, IconUsers } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { fetchRoles } from '@/features/roles/api';
import { fetchList } from '@/shared/api/jsonapi/client';

export const Route = createFileRoute('/_app/roles/')({ component: RolesListPage });

type Filter = 'all' | 'system' | 'custom';

function RolesListPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  const roles = useMemo(() => rolesQ.data?.data ?? [], [rolesQ.data]);

  // Conteggi claim + membri per ruolo (page size 1 → meta.total). Reti piccole, in parallelo.
  const statsQ = useQueries({
    queries: roles.map((r) => ({
      queryKey: ['role-stats', r.id],
      staleTime: 60_000,
      queryFn: async () => {
        const [claims, members] = await Promise.all([
          fetchList('role-claims', { filter: { 'role.id': { eq: r.id } }, page: { size: 1 } }),
          fetchList('user-roles', { filter: { 'role.id': { eq: r.id } }, page: { size: 1 } }),
        ]);
        return { claims: claims.total, members: members.total };
      },
    })),
  });

  const statsById = useMemo(() => {
    const m = new Map<string, { claims: number; members: number }>();
    roles.forEach((r, i) => { const d = statsQ[i]?.data; if (d) m.set(r.id, d); });
    return m;
  }, [roles, statsQ]);

  const systemCount = roles.filter((r) => r.isFromMaster).length;
  const customCount = roles.length - systemCount;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roles.filter((r) => {
      if (filter === 'system' && !r.isFromMaster) return false;
      if (filter === 'custom' && r.isFromMaster) return false;
      if (q && !(r.name ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [roles, filter, search]);

  const statsLoading = statsQ.some((q) => q.isLoading);

  return (
    <Stack>
      <Title order={1}>Ruoli</Title>

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard icon={IconShieldCheck} color="blue" label="Ruoli totali" value={roles.length} />
        <StatCard icon={IconLock} color="gray" label="Di sistema" value={systemCount} />
        <StatCard icon={IconKey} color="teal" label="Personalizzati" value={customCount} />
      </SimpleGrid>

      <Group justify="space-between" wrap="wrap">
        <SegmentedControl
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          data={[
            { value: 'all', label: 'Tutti' },
            { value: 'system', label: 'Sistema' },
            { value: 'custom', label: 'Personalizzati' },
          ]}
        />
        <TextInput
          placeholder="Cerca ruolo…"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={240}
        />
      </Group>

      <Card withBorder padding={0} radius="md">
        {rolesQ.isLoading ? (
          <Group p="lg"><Loader /></Group>
        ) : (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ruolo</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th ta="right">Claim {statsLoading && <Loader size={10} />}</Table.Th>
                <Table.Th ta="right">Membri</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visible.map((r) => {
                const s = statsById.get(r.id);
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <ThemeIcon variant="light" color={r.isFromMaster ? 'gray' : 'teal'} radius="md" size="sm">
                          <IconShieldCheck size={14} />
                        </ThemeIcon>
                        <Text fw={500}>{r.name ?? r.id}</Text>
                        {r.normalizedName && <Text size="xs" c="dimmed">{r.normalizedName}</Text>}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {r.isFromMaster
                        ? <Badge color="gray" variant="light" leftSection={<IconLock size={11} />}>Sistema</Badge>
                        : <Badge color="teal" variant="light">Personalizzato</Badge>}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Badge variant="light" color="blue">{s ? s.claims : '—'}</Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Badge variant="light" color="grape" leftSection={<IconUsers size={11} />}>{s ? s.members : '—'}</Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Button component={Link} to={`/roles/${r.id}`} size="xs" variant="subtle" rightSection={<IconArrowRight size={14} />}>
                        Apri
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {visible.length === 0 && (
                <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="lg">Nessun ruolo con questi filtri.</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}

function StatCard({ icon: Icon, color, label, value }: { icon: typeof IconKey; color: string; label: string; value: number }) {
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
          <Text fw={700} fz="xl">{value}</Text>
        </div>
        <ThemeIcon variant="light" color={color} radius="md" size="lg"><Icon size={20} /></ThemeIcon>
      </Group>
    </Card>
  );
}
